/*
useBrowserStorage.tsx
Copyright © 2026 Xerox Corporation. All Rights Reserved.

Copyright protection claimed includes all forms and matters of copyrightable material
and information now allowed by statutory or judicial law or hereinafter granted,
including without limitation, material generated from the software programs which
are displayed on the screen such as icons, screen display looks, etc.
*/

import { useState, useEffect } from 'react';
import { getLocalVar, setLocalVar } from "../modules/browserStorage";

// Define a generic type for `usePersistentState`
// `T` represents the type of the state
function usePersistentState<T>(storageKey: string, initialState: T): [T, React.Dispatch<React.SetStateAction<T>>] {
  // Define state and a setter function for internal state
  const [state, setInternalState] = useState<T>(initialState);

  useEffect(() => {
    const storageInBrowser = getLocalVar(storageKey);
    if (storageInBrowser) {
      try {
        const parsed = JSON.parse(storageInBrowser) as T;
        setInternalState(parsed);
      } catch (e) {
        console.error('Error parsing storage value:', e);
      }
    }
  }, [storageKey]);

  useEffect(() => {
    setLocalVar(storageKey, JSON.stringify(state));
  }, [state, storageKey]);

  const setState = (stateUpdater: React.SetStateAction<T>) => {
    setInternalState(stateUpdater);
  };

  return [state, setState];
}

export default usePersistentState;